import { compareLabels } from '@bre-ticketing/domain';
import type { ManifestGeometry, RowGeometry, SeatGeometry, SectionGeometry, TableGeometry } from './types';

/**
 * The manifest as a tree, for the accessible list view and for anything that
 * needs "which rows are in this section" without re-deriving it (spec §67:
 * the list exposes exactly the seats the canvas draws, from the same data).
 */

export interface SeatGroup {
  kind: 'row' | 'table';
  id: string;
  label: string;
  sort_order: number;
  seats: SeatGeometry[];
  table?: TableGeometry;
  row?: RowGeometry;
}

export interface SectionTree {
  section: SectionGeometry;
  level_name: string | null;
  groups: SeatGroup[];
  seat_count: number;
}

export function manifestTree(layout: ManifestGeometry): SectionTree[] {
  const levelName = new Map(layout.levels.map((level) => [level.id, level.name]));
  const rowsBySection = new Map<string, RowGeometry[]>();
  for (const row of layout.rows) {
    const list = rowsBySection.get(row.section_id) ?? [];
    list.push(row);
    rowsBySection.set(row.section_id, list);
  }
  const tablesBySection = new Map<string, TableGeometry[]>();
  for (const table of layout.tables) {
    const list = tablesBySection.get(table.section_id) ?? [];
    list.push(table);
    tablesBySection.set(table.section_id, list);
  }
  const seatsByGroup = new Map<string, SeatGeometry[]>();
  for (const seat of layout.seats) {
    const key = seat.row_id ?? seat.table_id;
    if (!key) continue;
    const list = seatsByGroup.get(key) ?? [];
    list.push(seat);
    seatsByGroup.set(key, list);
  }
  for (const list of seatsByGroup.values()) list.sort((a, b) => a.sort_order - b.sort_order);

  return [...layout.sections]
    .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code))
    .map((section) => {
      const groups: SeatGroup[] = [
        ...(rowsBySection.get(section.id) ?? []).map<SeatGroup>((row) => ({
          kind: 'row',
          id: row.id,
          label: row.label,
          sort_order: row.sort_order,
          seats: seatsByGroup.get(row.id) ?? [],
          row,
        })),
        ...(tablesBySection.get(section.id) ?? []).map<SeatGroup>((table) => ({
          kind: 'table',
          id: table.id,
          label: table.label,
          sort_order: 1_000_000,
          seats: seatsByGroup.get(table.id) ?? [],
          table,
        })),
      ]
        // Read the way a person reads them: table 2 before table 10, not
        // 1, 10, 11, 2. `localeCompare` on a bare string does the latter.
        .sort((a, b) => a.sort_order - b.sort_order || compareLabels(a.label, b.label));
      return {
        section,
        level_name: section.level_id ? (levelName.get(section.level_id) ?? null) : null,
        groups,
        seat_count: groups.reduce((total, group) => total + group.seats.length, 0),
      };
    });
}

/** "Floor 101, Row K, Seat 4 (wheelchair)" — what a screen reader should say. */
export function describeSeat(seat: SeatGeometry, group: SeatGroup, section: SectionGeometry): string {
  const where = group.kind === 'row' ? `Row ${group.label}` : `Table ${group.label}`;
  const notes: string[] = [];
  if (seat.accessible) notes.push('wheelchair accessible');
  if (seat.companion) notes.push('companion seat');
  if (seat.restricted_view) notes.push('restricted view');
  if (seat.obstructed_view) notes.push('obstructed view');
  if (!seat.active) notes.push('not in use');
  const suffix = notes.length ? ` (${notes.join(', ')})` : '';
  return `${section.name}, ${where}, Seat ${seat.seat_number}${suffix}`;
}
